import { CircleRounded, KeyboardArrowDownRounded } from '@mui/icons-material'
import { Box, Chip, Menu, MenuItem, Stack, SxProps, Typography } from '@mui/material'
import { FC, MouseEvent, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { AppDispatch } from '../../../store'
import { CompanyPreferencesSelector } from '../../../store/companyPreferences'
import { GrantMatchActions } from '../../../store/grantMatcher'

const menuSx: SxProps = {
  '& .MuiPaper-root': {
    mt: 0.5,
    minWidth: 120,
    height: 'min-content',
    borderRadius: '0.5rem',
    boxShadow: `rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px`,
  },
}

interface TrackPipelineStagesProps {
  cb?: Function
  refId: string
  currentStage: string
}

export const TrackPipelineStage: FC<TrackPipelineStagesProps> = ({ cb, refId, currentStage }) => {
  const { projectId = '' } = useParams()
  const dispatch = useDispatch<AppDispatch>()

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [newPipelineStage, setNewPipelineStage] = useState<string>('')

  const pipelineStages = useSelector(CompanyPreferencesSelector.makeGrantPipelineStages)

  const open = Boolean(anchorEl)

  useEffect(() => {
    setNewPipelineStage(currentStage)
  }, [currentStage])

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    const currentTarget = event.currentTarget

    event.stopPropagation()
    setAnchorEl(() => currentTarget)
  }

  const handleClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const handleUpdateGrantStatus = async (event: MouseEvent<HTMLElement>, stage: string) => {
    setNewPipelineStage(stage)
    handleClose(event)

    const resultAction = await dispatch(
      GrantMatchActions.updateMatchedTechGrantAsync({
        params: { techId: projectId, refId },
        body: { progressStatus: stage },
      }),
    )

    if (GrantMatchActions.updateMatchedTechGrantAsync.rejected.match(resultAction)) {
      setNewPipelineStage(stage)
    }

    cb && cb()
  }

  const getCurrentStatusColor = (currentStage: string) => {
    return pipelineStages.find((value) => value.stage === currentStage)
  }

  const item = getCurrentStatusColor(newPipelineStage)
  const { textColor = '', backgroundColor = '' } = item || {}

  return (
    <Box>
      <Box onClick={handleClick}>
        {newPipelineStage && (
          <Chip
            size="small"
            sx={{
              px: 1,
              my: '.2rem',
              height: '34px',
              minWidth: '100%',
              cursor:"pointer",
              borderRadius: '24px',
              background: backgroundColor || 'default',
            }}
            label={
              <Stack
                direction="row"
                justifyContent="center"
                alignItems="center"
                gap={0.6}
                width="100%"
              >
                <Box display="flex" alignItems="center">
                  <CircleRounded sx={{ mt:0.2,color: textColor, fontSize: '0.65rem' }} />
                </Box>
                <Typography
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.875rem',
                    textTransform: 'capitalize',
                    color: textColor || 'inherit',
                  }}
                >
                  {newPipelineStage}
                </Typography>

                <KeyboardArrowDownRounded sx={{ mt:0.8 ,color: textColor }}/>
              </Stack>
            }
          />
        )}
      </Box>

      <Menu
        open={open}
        sx={menuSx}
        elevation={0}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {pipelineStages &&
          pipelineStages.map(({ stage, index, textColor, backgroundColor }) => {
            return (
              stage !== newPipelineStage && (
                <MenuItem
                  key={index}
                  disableRipple
                  onClick={(e) => {
                    handleUpdateGrantStatus(e, stage)
                  }}
                  sx={{ display: 'flex', justifyContent: 'center' }}
                >
                  <Chip
                    size="small"
                    sx={{
                      minWidth: '100px',
                      borderRadius: '5px',
                      background: backgroundColor,
                    }}
                    label={
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: textColor,
                          fontSize: '0.875rem',
                          textTransform: 'capitalize',
                        }}
                      >
                        {stage}
                      </Typography>
                    }
                  />
                </MenuItem>
              )
            )
          })}
      </Menu>
    </Box>
  )
}
